import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ClipboardList,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Building2,
  Mail,
  Phone,
  Briefcase,
  MessageSquare,
  ChevronRight,
  AlertCircle,
  Loader2,
  RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [selectedApp, setSelectedApp] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalNotes, setApprovalNotes] = useState("");

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(
        `${API_URL}/api/applications/admin/list?status=${filter === "all" ? "" : filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApplications(response.data.applications || []);
      setStats({
        pending: response.data.pending_count || 0,
        approved: response.data.approved_count || 0,
        rejected: response.data.rejected_count || 0
      });
      setError(null);
    } catch (err) {
      console.error("Failed to fetch applications:", err);
      setError("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const handleApprove = async (applicationId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API_URL}/api/applications/admin/${applicationId}/approve`,
        { approved: true, notes: approvalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedApp(null);
      setApprovalNotes("");
      fetchApplications();
    } catch (err) {
      console.error("Failed to approve:", err);
      alert("Failed to approve application: " + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (applicationId) => {
    if (!rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }
    setProcessing(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API_URL}/api/applications/admin/${applicationId}/reject`,
        { approved: false, rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedApp(null);
      setRejectionReason("");
      fetchApplications();
    } catch (err) {
      console.error("Failed to reject:", err);
      alert("Failed to reject application: " + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const statusColors = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    rejected: "bg-red-500/10 text-red-400 border-red-500/30"
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-blue-400" />
              Customer Applications
            </h1>
            <p className="text-slate-400 mt-1">Review and approve new customer applications</p>
          </div>
          <Button
            variant="outline"
            className="border-slate-700 text-slate-300"
            onClick={fetchApplications}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card 
            className={`bg-slate-900/50 border-slate-800 cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => setFilter("pending")}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.pending}</p>
                <p className="text-sm text-slate-400">Pending Review</p>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`bg-slate-900/50 border-slate-800 cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-emerald-500' : ''}`}
            onClick={() => setFilter("approved")}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.approved}</p>
                <p className="text-sm text-slate-400">Approved</p>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`bg-slate-900/50 border-slate-800 cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setFilter("rejected")}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.rejected}</p>
                <p className="text-sm text-slate-400">Rejected</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applications List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List Panel */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {filter === "pending" ? "Pending" : filter === "approved" ? "Approved" : filter === "rejected" ? "Rejected" : "All"} Applications
              </h2>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-400"
                onClick={() => setFilter("all")}
              >
                View All
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No {filter !== "all" ? filter : ""} applications found
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map((app) => (
                  <Card
                    key={app.id}
                    className={`bg-slate-900/50 border-slate-800 cursor-pointer transition-all hover:border-slate-700 ${
                      selectedApp?.id === app.id ? "ring-2 ring-blue-500" : ""
                    }`}
                    onClick={() => setSelectedApp(app)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-white">{app.business_name}</h3>
                            <Badge className={statusColors[app.status]}>
                              {app.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400">{app.owner_name}</p>
                          <p className="text-xs text-slate-500 mt-1">{app.industry}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">{formatDate(app.applied_at)}</p>
                          <ChevronRight className="w-4 h-4 text-slate-600 mt-2 ml-auto" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div>
            {selectedApp ? (
              <Card className="bg-slate-900/50 border-slate-800 sticky top-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white">{selectedApp.business_name}</CardTitle>
                    <Badge className={statusColors[selectedApp.status]}>
                      {selectedApp.status}
                    </Badge>
                  </div>
                  <CardDescription>Application ID: {selectedApp.id.slice(0, 8)}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Owner Name</Label>
                      <p className="text-white flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        {selectedApp.owner_name}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Industry</Label>
                      <p className="text-white flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-slate-500" />
                        {selectedApp.industry}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Email</Label>
                      <p className="text-white flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <a href={`mailto:${selectedApp.email}`} className="text-blue-400 hover:underline">
                          {selectedApp.email}
                        </a>
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Phone</Label>
                      <p className="text-white flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-500" />
                        {selectedApp.phone || "Not provided"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Services Requested</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedApp.services_requested?.map((service, idx) => (
                        <Badge key={idx} variant="outline" className="text-slate-300 border-slate-700">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Estimated Call Volume</Label>
                    <p className="text-white">{selectedApp.estimated_call_volume || "Not specified"}</p>
                  </div>

                  {selectedApp.notes && (
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Additional Notes</Label>
                      <p className="text-slate-300 bg-slate-800/50 p-3 rounded-lg text-sm">
                        {selectedApp.notes}
                      </p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Applied At</Label>
                    <p className="text-white">{formatDate(selectedApp.applied_at)}</p>
                  </div>

                  {selectedApp.status === "pending" && (
                    <div className="border-t border-slate-800 pt-4 space-y-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Approval Notes (optional)</Label>
                        <Input
                          placeholder="Internal notes..."
                          className="bg-slate-800/50 border-slate-700 text-white"
                          value={approvalNotes}
                          onChange={(e) => setApprovalNotes(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-300">Rejection Reason (required to reject)</Label>
                        <Input
                          placeholder="Reason for rejection..."
                          className="bg-slate-800/50 border-slate-700 text-white"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(selectedApp.id)}
                          disabled={processing}
                        >
                          {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleReject(selectedApp.id)}
                          disabled={processing}
                        >
                          {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}

                  {selectedApp.status === "approved" && selectedApp.reviewed_at && (
                    <div className="border-t border-slate-800 pt-4">
                      <p className="text-sm text-emerald-400">
                        ✓ Approved on {formatDate(selectedApp.reviewed_at)}
                      </p>
                      {selectedApp.approval_notes && (
                        <p className="text-sm text-slate-400 mt-1">Notes: {selectedApp.approval_notes}</p>
                      )}
                    </div>
                  )}

                  {selectedApp.status === "rejected" && (
                    <div className="border-t border-slate-800 pt-4">
                      <p className="text-sm text-red-400">
                        ✗ Rejected on {formatDate(selectedApp.reviewed_at)}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        Reason: {selectedApp.rejection_reason}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-500">
                <div className="text-center">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Select an application to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
